import {
  FunctionComponent,
  PropsWithChildren,
  useEffect,
  useMemo,
} from 'react';

import { getIn } from 'final-form';
import { Field, useForm, useFormState } from 'react-final-form';

import {
  SxProps,
  Typography,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@cast/design-system';

import { RffInput } from '../adapters';
import { CUSTOM_PRESET } from './constants';
import { DurationUnit, DurationUnitKey, Preset } from './types';
import { useControlsGroupState } from '../adapters/controls-state';
import { useRffFormState } from '../hooks';
import { findMatchingPreset, resolveUnit } from './utils';

export type DurationPresetInputProps = {
  units: Array<DurationUnitKey | DurationUnit>;
  presets: Preset[];
  label?: string;
  disabled?: boolean;
  testId?: string;
  LabelComponent?: FunctionComponent<PropsWithChildren>;
  sx?: SxProps;
};

export const DurationPresetInput = ({
  units,
  presets,
  label,
  disabled: _disabled,
  testId,
  LabelComponent,
  sx,
}: DurationPresetInputProps) => {
  const { change } = useForm();
  const { mode } = useRffFormState();
  const { errors, submitErrors, values } = useFormState({
    subscription: { errors: true, submitErrors: true, values: true },
  });

  const { disabled, readOnly, namePrefix } = useControlsGroupState({
    disabled: _disabled,
    readOnly: mode === 'view',
  });

  const isInteractionDisabled = disabled || readOnly;

  const normalizedUnits = useMemo(() => units.map(resolveUnit), [units]);

  const fieldNames = normalizedUnits.reduce((acc, { key, formAccessor }) => {
    acc[key] = namePrefix ? `${namePrefix}.${formAccessor}` : formAccessor;
    return acc;
  }, {} as Record<DurationUnitKey, string>);

  const isCustomFieldName = namePrefix ? `${namePrefix}.isCustom` : 'isCustom';
  const isCustomMode: boolean = !!getIn(values, isCustomFieldName);

  const matchedPreset = !isCustomMode
    ? findMatchingPreset({ values, presets, fieldNames, normalizedUnits })
    : null;

  const activePreset = isCustomMode ? CUSTOM_PRESET : matchedPreset;

  const allPresets = useMemo(() => [...presets, CUSTOM_PRESET], [presets]);

  // If no preset matches and isCustom is not set, sync it into the form
  useEffect(() => {
    if (!isCustomMode && matchedPreset === null) {
      change(isCustomFieldName, true);
    }
  }, [isCustomMode, matchedPreset, change, isCustomFieldName]);

  const sharedError = useMemo(() => {
    for (const { key } of normalizedUnits) {
      const error =
        getIn(errors ?? {}, fieldNames[key]) ||
        getIn(submitErrors ?? {}, fieldNames[key]);

      if (error) {
        return error;
      }
    }

    return null;
  }, [normalizedUnits, errors, submitErrors, fieldNames]);

  const handlePresetSelect = (preset: Preset) => {
    if (isInteractionDisabled) {
      return;
    }

    if (preset === CUSTOM_PRESET) {
      change(isCustomFieldName, true);
    } else {
      change(isCustomFieldName, false);
      normalizedUnits.forEach(({ key }) => {
        change(fieldNames[key], preset[key]);
      });
    }
  };

  const renderedLabel = label ? (
    LabelComponent ? (
      <LabelComponent>{label}</LabelComponent>
    ) : (
      <Typography variant="P12M">{label}</Typography>
    )
  ) : null;

  return (
    <Stack gap={4} data-testid={testId} sx={sx}>
      {renderedLabel}
      <Stack gap={8} direction="row" alignItems="flex-start">
        <ToggleButtonGroup
          size="md"
          exclusive
          value={activePreset?.label ?? null}
          disabled={isInteractionDisabled}
          testId="presets-container"
          onChange={(_event, value: string) => {
            const preset = allPresets.find((p) => p.label === value);
            if (preset) {
              handlePresetSelect(preset);
            }
          }}
        >
          {allPresets.map((preset) => (
            <ToggleButton
              key={preset.label}
              value={preset.label}
              text={preset.label}
              testId={`preset-${preset.label}`}
              data-active={activePreset === preset}
            />
          ))}
        </ToggleButtonGroup>

        {activePreset === CUSTOM_PRESET && (
          <Stack gap={8}>
            <Stack gap={8} direction="row" alignItems="flex-start">
              {normalizedUnits.map(({ key, formAccessor, min, max, label }) => (
                <RffInput
                  key={key}
                  sx={{ width: 80 }}
                  name={formAccessor}
                  type="number"
                  size="small"
                  testId={`custom-input-${key}`}
                  disabled={isInteractionDisabled}
                  min={min}
                  max={max}
                  withSpinButtons
                  fieldProps={{ subscription: { value: true } }}
                  endAdornment={
                    <Typography variant="P12R" color="textSecondary">
                      {label}
                    </Typography>
                  }
                />
              ))}
            </Stack>

            {sharedError && (
              <Typography variant="P12R" color="error.main">
                {sharedError}
              </Typography>
            )}
          </Stack>
        )}
      </Stack>

      {/*
        Keep unit fields registered even when the custom inputs are hidden in
        preset mode. react-final-form only reliably propagates dirty state for
        fields that are registered, so selecting a preset (which calls
        form.change on these fields) would not mark the form dirty otherwise.
      */}
      {normalizedUnits.map(({ key }) => (
        <Field
          key={`hidden-${key}`}
          name={fieldNames[key]}
          subscription={{}}
          render={() => null}
        />
      ))}
    </Stack>
  );
};
